import React from 'react';
import ReactEcharts from 'echarts-for-react';
import {
    checkIsMetricByYear,
    translateMultipleName,
} from 'routes/BudgetModule/helpers/format';
import {
    ARBITRAGE_DM_AMOUNT,
    SUPPLEMENTARY_BUDGET_AMOUNT,
} from 'routes/BudgetModule/config/constants';
import { CadrageEchartProps, customizePaneTooltip } from './eChartsHelpers';
import { translateComponent } from 'i18n';

type PaneSettingsT = {
    yAxisName: string;
    metrics: string[];
};

type Props = {
    paneSettings: PaneSettingsT;
    data: ResponseDataT[];
    selectedDimension: string;
    height: number;
    width: number;
    exercice: number;
};

const barColors = ['#7689ad', '#30475e', '#7c8ed6'];

const yAxisLabelFormatter = (value: number) => {
    if (value >= 1_000_000) {
        return `${(value / 1_000_000).toFixed(1)} M€`;
    } else if (value >= 1_000) {
        return `${(value / 1_000).toFixed(1)} k€`;
    } else {
        return `${value.toFixed(1)} €`;
    }
};

const translateMetricName = (metric: string, exercice: number): string => {
    // In the supplementary phase, the sum of the inscription
    // represents the total budget not the supplementary budget
    if ([SUPPLEMENTARY_BUDGET_AMOUNT, ARBITRAGE_DM_AMOUNT].includes(metric)) {
        const metricName = translateComponent('budgetProposal.totalBudget');
        return `${metricName} ${exercice}`;
    }
    // current phase metric does not have a year, so we add one just for the display
    const metricByYear = checkIsMetricByYear(metric)
        ? metric
        : `${metric}..${exercice}`;
    return translateMultipleName(metricByYear);
};

const MultiplePaneChart: React.FC<Props> = ({
    selectedDimension,
    data,
    paneSettings,
    height,
    width,
    exercice,
}) => {
    const dataSets: { label: string; value: number }[][] = [];

    const metrics = paneSettings.metrics.map((metric) => metric);

    const translatedMetrics = metrics.map((metric) =>
        translateMetricName(metric, exercice),
    );

    const filteredData = data.filter(
        (d) => d[metrics[0]] || d[metrics[1]] || d[metrics[2]],
    );

    metrics.map((metric) =>
        dataSets.push(
            filteredData.map((d) => ({
                label: d[selectedDimension] as string,
                value: d[metric] as number,
            })),
        ),
    );

    const option = {
        tooltip: {
            trigger: 'axis',
            formatter: (value: CadrageEchartProps[]) =>
                customizePaneTooltip({ values: value }),
        },
        legend: {
            data: translatedMetrics,
        },
        xAxis: {
            data: filteredData.map((d) => d[selectedDimension]),
        },
        yAxis: {
            axisLabel: {
                formatter: yAxisLabelFormatter,
            },
        },
        series: [
            {
                data: dataSets[0],
                type: 'bar',
                name: translatedMetrics[0],
                itemStyle: { color: barColors[0] },
            },
            {
                data: dataSets[1],
                type: 'bar',
                name: translatedMetrics[1],
                itemStyle: { color: barColors[1] },
            },
            {
                data: dataSets[2],
                type: 'bar',
                name: translatedMetrics[2],
                itemStyle: { color: barColors[2] },
            },
        ],
        grid: {
            left: '5%',
            right: '5%',
            bottom: '2%',
            containLabel: true,
        },
    };

    return <ReactEcharts option={option} style={{ height, width }} />;
};

export default MultiplePaneChart;
